import { computed, ref, watch, onMounted, onBeforeUnmount, useHost } from "vue";
import type { ComputedRef, Ref } from "vue";
import type { DialogProps } from "./dialog.types";
import { dialogBaseClass } from "./dialog.styles";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { useSharedState } from "composables/useSharedState";
import { createEventDispatcher } from "@/runtime/shared/events";
import { parseBoolAttr } from "@/runtime/shared/attrs";

const ANIM_OPTIONS: KeyframeAnimationOptions = { duration: 150, easing: "ease-out", fill: "forwards" };
const DIALOG_IN: Keyframe[] = [{ opacity: 0 }, { opacity: 1 }];
const DIALOG_OUT: Keyframe[] = [{ opacity: 1 }, { opacity: 0 }];
const CONTENT_IN: Keyframe[] = [
  { opacity: 0, transform: "translateY(12px)" },
  { opacity: 1, transform: "translateY(0)" },
];
const CONTENT_OUT: Keyframe[] = [
  { opacity: 1, transform: "translateY(0)" },
  { opacity: 0, transform: "translateY(12px)" },
];

const runAnimations = (el: HTMLDialogElement, dialogKf: Keyframe[], contentKf: Keyframe[]): Animation[] => {
  const anims: Animation[] = [el.animate(dialogKf, ANIM_OPTIONS)];
  const slot = el.querySelector<HTMLSlotElement>(":scope > slot");
  const target = slot?.assignedElements?.()?.[0] as HTMLElement | undefined;
  if (target) anims.push(target.animate(contentKf, ANIM_OPTIONS));
  return anims;
};

type DialogState = ReturnType<typeof useSharedState<DialogProps>>;
type Dispatcher = ReturnType<typeof createEventDispatcher>;

interface AnimCtx {
  active: Animation[];
  cancel: () => void;
}

const makeAnimCtx = (): AnimCtx => {
  const ctx: AnimCtx = {
    active: [],
    cancel: () => {
      for (const a of ctx.active) a.cancel();
      ctx.active = [];
    },
  };
  return ctx;
};

const makeOpen =
  (
    anim: AnimCtx,
    internalOpen: Ref<boolean>,
    isModal: ComputedRef<boolean>,
    isAnimated: ComputedRef<boolean>,
    contentRef: Ref<HTMLDialogElement | null> | undefined,
    dispatchEvent: Dispatcher,
  ) =>
  () => {
    if (internalOpen.value) return;
    anim.cancel();
    internalOpen.value = true;
    dispatchEvent("update:open", true);
    dispatchEvent("open");
    const el = contentRef?.value;
    if (!el) return;
    if (isModal.value) el.showModal();
    else el.show();
    requestAnimationFrame(() => {
      if (isAnimated.value) anim.active = runAnimations(el, DIALOG_IN, CONTENT_IN);
    });
  };

const makeClose =
  (
    anim: AnimCtx,
    internalOpen: Ref<boolean>,
    isAnimated: ComputedRef<boolean>,
    contentRef: Ref<HTMLDialogElement | null> | undefined,
    host: Element | null,
    dispatchEvent: Dispatcher,
  ) =>
  () => {
    if (!internalOpen.value) return;
    internalOpen.value = false;
    host?.removeAttribute("open");
    dispatchEvent("update:open", false);
    dispatchEvent("close");
    const el = contentRef?.value;
    if (!el) return;
    if (!isAnimated.value) {
      el.close();
      return;
    }
    anim.cancel();
    anim.active = runAnimations(el, DIALOG_OUT, CONTENT_OUT);
    anim.active[0].finished.then(() => {
      anim.active = [];
      el.close();
    });
  };

const setupEscapeDismiss = (
  contentRef: Ref<HTMLDialogElement | null> | undefined,
  state: DialogState,
  close: () => void,
) => {
  if (!contentRef) return;
  const handle = (e: Event) => {
    // Prevent native close so our close() handles state and animations
    e.preventDefault();
    if (parseBoolAttr(state.closeOnEscape?.value, true)) close();
  };
  watch(
    contentRef,
    (el, _, onCleanup) => {
      if (!el) return;
      el.addEventListener("cancel", handle);
      onCleanup(() => el.removeEventListener("cancel", handle));
    },
    { immediate: true },
  );
};

export interface UseDialogOptions {
  props: Readonly<DialogProps>;
  contentRef?: Ref<HTMLDialogElement | null>;
}
export interface UseDialogReturn {
  classes: ComputedRef<string>;
  isOpen: ComputedRef<boolean>;
  isModal: ComputedRef<boolean>;
  open: () => void;
  close: () => void;
  onBackdropClick: () => void;
}

export const useDialog = (options: UseDialogOptions): UseDialogReturn => {
  const { props } = options;
  const host = useHost();
  const state = useSharedState(props);
  const dispatchEvent = createEventDispatcher(host);
  const internalOpen = ref(parseBoolAttr(state.open?.value, false));
  const isOpen = computed(() => internalOpen.value);
  const isModal = computed(() => parseBoolAttr(state.modal?.value, true));
  const isAnimated = computed(() => parseBoolAttr(state.animated?.value, true));
  const classes = computed(() => mergeClassProp(dialogBaseClass, props.class));
  const anim = makeAnimCtx();
  const open = makeOpen(anim, internalOpen, isModal, isAnimated, options.contentRef, dispatchEvent);
  const close = makeClose(anim, internalOpen, isAnimated, options.contentRef, host, dispatchEvent);

  // Handle initial open state on mount (no animation for pre-opened dialogs)
  onMounted(() => {
    if (!internalOpen.value) return;
    const el = options.contentRef?.value;
    if (!el) return;
    if (isModal.value) el.showModal();
    else el.show();
  });

  watch(
    () => state.open?.value,
    (v) => {
      const next = parseBoolAttr(v, false);
      if (next && !internalOpen.value) open();
      else if (!next && internalOpen.value) close();
    },
    { flush: "post" },
  );

  setupEscapeDismiss(options.contentRef, state, close);

  onBeforeUnmount(() => {
    anim.cancel();
    options.contentRef?.value?.close();
  });

  const onBackdropClick = () => {
    if (isOpen.value && isModal.value && parseBoolAttr(state.closeOnClickOutside?.value, true)) close();
  };

  return { classes, isOpen, isModal, open, close, onBackdropClick };
};
